"use client";

import { useEffect, useState } from "react";
import { listPublicAdmissionIntakes } from "@/actions/admission-public";

const MY_DIGITS = ["၀", "၁", "၂", "၃", "၄", "၅", "၆", "၇", "၈", "၉"];
const toMyDigits = (s: string) => s.replace(/\d/g, (d) => MY_DIGITS[Number(d)]);
const fmtDate = (d: string | null | undefined, lang: "en" | "mm") => {
  if (!d) return "";
  const t = new Date(d).getTime();
  if (Number.isNaN(t)) return "";
  const y = new Date(t + 6.5 * 3600 * 1000); // Yangon wall time — same on server and browser
  const pad = (n: number) => String(n).padStart(2, "0");
  const s = `${pad(y.getUTCDate())}.${pad(y.getUTCMonth() + 1)}.${y.getUTCFullYear()}`;
  return lang === "mm" ? toMyDigits(s) : s;
};

/** One published admission round, ready to render. */
export interface EnrollmentRoundView {
  id: string;
  /** admission | re-enrollment | roster — lets a theme name the list link. */
  kind: "admission" | "re-enrollment" | "roster";
  label: string;
  /** "dd.mm.yyyy မှ dd.mm.yyyy အထိ" or "" */
  window: string;
  listHref: string | null;
  enrollHref: string;
}

export interface EnrollmentBannerData {
  lang: "en" | "mm";
  /** Eyebrow / section title. */
  title: string;
  rounds: EnrollmentRoundView[];
  t: {
    windowPrefix: string;
    list: string;
    enroll: string;
  };
}

/**
 * Data side of the home enrollment banner (section type `enrollmentGate`),
 * shared by every theme — each theme only supplies its own look.
 *
 * Follows the PUBLISHED admission announcements: returns null while loading
 * or when nothing is published (the banner then renders nothing).
 * cmsAdmin overrides: `batchId` pins one round; `batchLabel`/`deadlineText`
 * override the texts when a single round shows.
 */
export function useEnrollmentRounds(
  section: any,
  currentLanguage: "en" | "mm" = "mm",
): EnrollmentBannerData | null {
  const lang = currentLanguage === "en" ? "en" : "mm";
  const pinnedBatchId: string | undefined = section?.batchId || undefined;
  const [raw, setRaw] = useState<Awaited<ReturnType<typeof listPublicAdmissionIntakes>> | null>(null);

  useEffect(() => {
    let cancelled = false;
    listPublicAdmissionIntakes()
      .then((r) => !cancelled && setRaw(r))
      .catch(() => !cancelled && setRaw(null));
    return () => {
      cancelled = true;
    };
  }, []);

  const all = raw?.success && raw.data ? raw.data : [];
  const list = pinnedBatchId ? all.filter((i) => i.batch?.id === pinnedBatchId) : all;
  if (list.length === 0) return null;

  const single = list.length === 1;
  const labelOverride: string | undefined = section?.batchLabel || undefined;
  const deadlineOverride: string | undefined =
    section?.deadlineText?.[lang] || section?.deadlineText?.mm || section?.deadlineText?.en || undefined;

  const rounds: EnrollmentRoundView[] = list.map((r) => {
    const from = fmtDate(r.registrationWindow?.from, lang);
    const to = fmtDate(r.registrationWindow?.to, lang);
    const window =
      (single && deadlineOverride) ||
      (from && to
        ? lang === "en" ? `${from} – ${to}` : `${from} မှ ${to} အထိ`
        : from || to);
    return {
      id: r.id,
      kind: r.kind ?? "admission",
      label:
        (single && labelOverride) ||
        r.batch?.displayName?.[lang] ||
        r.batch?.displayName?.mm ||
        r.batch?.name ||
        r.name,
      window,
      listHref: r.slug ? `/admission/${encodeURIComponent(r.slug)}` : null,
      enrollHref: `/enroll${r.batch?.id ? `?batch=${r.batch.id}` : ""}`,
    };
  });

  return {
    lang,
    title:
      section?.title?.[lang] ||
      section?.title?.mm ||
      (lang === "en" ? "New Student Enrollment" : "ကျောင်းသားသစ် ကျောင်းအပ်ရန်"),
    rounds,
    t:
      lang === "en"
        ? { windowPrefix: "Enrollment:", list: "Admission list", enroll: "Enroll now" }
        : { windowPrefix: "လက်ခံမည့်ကာလ —", list: "ဝင်ခွင့်ရစာရင်း", enroll: "ကျောင်းအပ်ရန်" },
  };
}
